import {
  getCarNumberMatchKey,
  hasReservationTimeConflict,
  normalizeCarNumber,
} from "../utils/dispatchConflictUtils.js";

export function normalizeSeatCount(value) {
  const match = String(value ?? "").match(/\d+/);
  return match ? Number(match[0]) : null;
}

function inferSeatCountFromSeatType(value) {
  const seatType = String(value ?? "").trim();
  if (/대형/.test(seatType)) return 40;
  if (/우등/.test(seatType)) return 28;
  if (/중형/.test(seatType)) return 20;
  return null;
}

function getCarSeatCount(car) {
  return (
    normalizeSeatCount(car?.seat_detail) ??
    normalizeSeatCount(car?.seat) ??
    inferSeatCountFromSeatType(car?.seat)
  );
}

function getPrimaryCarNumber(car) {
  return normalizeCarNumber(car?.car_number_only || car?.car_number);
}

function buildCarMatchKeys(car) {
  return new Set(
    [car?.car_number, car?.car_number_only]
      .map((value) => getCarNumberMatchKey(value))
      .filter(Boolean)
  );
}

function formatAvailableCar(car, seatCount) {
  return {
    carNumber: getPrimaryCarNumber(car),
    fullCarNumber: normalizeCarNumber(car.car_number),
    carName: car.car_name || "",
    seatType: car.seat || "",
    seatCount,
    year: car.year ?? "",
    driver: car.driver || "",
    phone: car.phone || "",
    company: car.company || "",
    available: true,
  };
}

function formatUnavailableCar(car, seatCount, reservation) {
  return {
    carNumber: getPrimaryCarNumber(car),
    fullCarNumber: normalizeCarNumber(car.car_number),
    carName: car.car_name || "",
    seatType: car.seat || "",
    seatCount,
    year: car.year ?? "",
    driver: car.driver || "",
    phone: car.phone || "",
    company: car.company || "",
    available: false,
    reason: "TIME_CONFLICT",
    conflictingReservation: {
      date: reservation.date,
      time: reservation.time,
      arrivalTime: reservation.arrival_time || null,
      carNumber: reservation.car_number || "",
      driver: reservation.human_name || "",
      phone: reservation.phone || "",
      seatType: reservation.seat || "",
      seatDetail: reservation.seat_detail || "",
    },
  };
}

function buildAvailableBusGroups(available, passengerCount) {
  const groups = [];
  const byCompany = new Map();
  available.forEach((bus) => {
    const company = bus.company || "";
    byCompany.set(company, [...(byCompany.get(company) || []), bus]);
  });

  byCompany.forEach((companyBuses) => {
    const sorted = [...companyBuses].sort((a, b) => {
      const seatDiff = Number(a.seatCount || 0) - Number(b.seatCount || 0);
      if (seatDiff !== 0) return seatDiff;
      return String(a.carNumber || "").localeCompare(String(b.carNumber || ""), "ko");
    });

    for (let i = 0; i < sorted.length; i += 1) {
      const buses = [];
      let totalSeats = 0;

      for (let j = i; j < sorted.length; j += 1) {
        buses.push(sorted[j]);
        totalSeats += Number(sorted[j].seatCount || 0);
        if (totalSeats >= passengerCount) break;
      }

      if (totalSeats >= passengerCount) {
        groups.push({
          buses,
          busCount: buses.length,
          totalSeats,
          remainingSeats: totalSeats - passengerCount,
          company: buses[0]?.company || "",
          available: true,
        });
      }
    }
  });

  return groups
    .sort((a, b) => {
      if (a.busCount !== b.busCount) return a.busCount - b.busCount;
      if (a.remainingSeats !== b.remainingSeats) return a.remainingSeats - b.remainingSeats;
      return b.totalSeats - a.totalSeats;
    })
    .slice(0, 10);
}

export function searchAvailableBusesFromData({
  cars = [],
  reservations = [],
  date,
  startTime,
  endTime,
  passengers,
}) {
  if (!date) throw new Error("date is required");
  if (!startTime) throw new Error("startTime is required");

  const passengerCount = Number(passengers);
  if (!Number.isFinite(passengerCount) || passengerCount <= 0) {
    throw new Error("passengers must be a positive number");
  }

  const dateReservations = reservations.filter((reservation) => String(reservation.date || "").slice(0, 10) === date);
  const requestReservation = {
    date,
    time: startTime,
    arrival_time: endTime || null,
  };

  const available = [];
  const unavailable = [];
  const diagnostics = {
    carsTotal: cars.length,
    reservationsTotal: dateReservations.length,
    passengerCount,
    seatMatched: 0,
    seatUnknown: 0,
    seatTooSmall: 0,
    timeConflict: 0,
  };

  cars.forEach((car) => {
    const seatCount = getCarSeatCount(car);
    if (seatCount === null) {
      diagnostics.seatUnknown += 1;
      return;
    }
    if (seatCount < 1) {
      diagnostics.seatTooSmall += 1;
      return;
    }
    diagnostics.seatMatched += 1;

    const carKeys = buildCarMatchKeys(car);
    const conflictingReservation = dateReservations.find((reservation) => {
      if (car.company && reservation.company && car.company !== reservation.company) return false;
      const reservationKey = getCarNumberMatchKey(reservation.car_number);
      return carKeys.has(reservationKey) && hasReservationTimeConflict(requestReservation, reservation);
    });

    if (conflictingReservation) {
      diagnostics.timeConflict += 1;
      unavailable.push(formatUnavailableCar(car, seatCount, conflictingReservation));
    } else {
      available.push(formatAvailableCar(car, seatCount));
    }
  });

  const singleVehicleAvailable = available.filter((bus) => Number(bus.seatCount || 0) >= passengerCount);
  const availableGroups = buildAvailableBusGroups(available, passengerCount);

  return {
    available: singleVehicleAvailable,
    unavailable,
    availableGroups,
    diagnostics: {
      ...diagnostics,
      singleVehicleAvailable: singleVehicleAvailable.length,
      availableSeatTotal: available.reduce((sum, bus) => sum + Number(bus.seatCount || 0), 0),
      availableGroups: availableGroups.length,
    },
  };
}
